import moment from "moment";
import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";
import * as XLSX from "xlsx";
import { HeaderEmpresaCard } from "../../../../Components/PdfComponents/HeaderEmpresaCard";
import { RESULTADO_LABEL } from "./reporteCalidadStyles";

pdfMake.vfs = pdfFonts.pdfMake.vfs;


export const exportarExcel = (nombreArchivo, nombreHoja, filas) => {
  const datos = filas.length > 0 ? filas : [{ "Sin datos": "" }];
  const columnas = [...new Set(datos.flatMap((d) => Object.keys(d)))];
  const ws = XLSX.utils.json_to_sheet(datos, { header: columnas });

  ws["!cols"] = columnas.map((k) => ({
    wch: Math.min(
      60,
      Math.max(k.length, ...datos.map((d) => String(d[k] ?? "").length)) + 2
    ),
  }));

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, nombreHoja);
  XLSX.writeFile(wb, `${nombreArchivo}_${moment().format("YYYY-MM-DD")}.xlsx`);
};

export const urlEvidencia = (url) => {
  if (!url) return "";
  if (/^https?:\/\//i.test(url)) return url;
  return `${window.location.origin}/${String(url).replace(/^\/+/, "")}`;
};

const PDF_STYLES = {
  header: { fontSize: 9, bold: true },
  subheader: { fontSize: 8, bold: true },
  tableExample: { margin: [0, 5, 0, 5] },
  tableHeader: { bold: true, fontSize: 12, color: "black", alignment: "center" },
  textoChico: { fontSize: 8 },
  tituloTabla: { fontSize: 8, bold: true, fillColor: "#cccccc", alignment: "center" },
  textoTabla: { fontSize: 8 },
  totalChico: { fontSize: 6, bold: true, fillColor: "#cccccc", alignment: "right" },
};

const LAYOUT_ZEBRA = {
  fillColor: (i) => (i % 2 === 0 && i !== 0 ? "#e8e8e8" : "#ffffff"),
  hLineColor: () => "#adaaaa",
  vLineColor: () => "#adaaaa",
  hLineStyle: (i, node) =>
    i === 0 || i === node.table.body.length ? null : { dash: { length: 2, space: 1 } },
  vLineStyle: (i, node) =>
    i === 0 || i === node.table.widths.length ? null : { dash: { length: 2 } },
};

const LAYOUT_SIMPLE = { hLineColor: () => "#adaaaa", vLineColor: () => "#adaaaa" };

const tablaKpis = (kpis) => {
  const visibles = kpis.filter(Boolean);
  if (visibles.length === 0) return [];
  return [
    { text: "Resumen del período", style: "subheader", margin: [0, 4, 0, 4] },
    {
      style: "tableExample",
      table: {
        widths: visibles.map(() => "*"),
        body: [
          visibles.map((k) => ({ text: k.label, style: "tituloTabla" })),
          visibles.map((k) => ({
            text: String(k.value),
            style: "totalChico",
            alignment: "center",
            fontSize: 13,
          })),
        ],
      },
      layout: LAYOUT_SIMPLE,
    },
  ];
};

const seccionTabla = (s) => {
  if (!s) return [];
  if (!s.filas || s.filas.length === 0) {
    return [
      { text: s.titulo, style: "subheader", fontSize: 11, margin: [0, 14, 0, 4] },
      { text: s.vacio ?? "Sin datos en el período.", style: "textoChico" },
    ];
  }
  return [
    { text: s.titulo, style: "subheader", fontSize: 11, margin: [0, 14, 0, 4] },
    {
      style: "tableExample",
      table: {
        headerRows: 1,
        widths: s.widths ?? s.columnas.map(() => "*"),
        body: [
          s.columnas.map((c) => ({ text: c, style: "tituloTabla" })),
          ...s.filas.map((fila) =>
            fila.map((celda) =>
              typeof celda === "object" && celda !== null
                ? { style: "textoTabla", ...celda }
                : { text: celda ?? "-", style: "textoTabla" }
            )
          ),
        ],
      },
      layout: LAYOUT_ZEBRA,
    },
  ];
};


export const barrasPdf = (titulo, barras, totalRef) => {
  const items = (barras || []).filter((b) => b.val > 0);
  if (items.length === 0) return [];

  const MAX_W = 210;
  const H = 11;
  const GAP = 7;
  const total = totalRef || Math.max(...items.map((b) => b.val));

  return [
    {
      columns: [
        {
          width: "45%",
          stack: [
            { text: titulo, style: "subheader", margin: [0, 0, 0, 6] },
            {
              columns: [
                {
                  width: MAX_W,
                  canvas: items.map((b, i) => ({
                    type: "rect",
                    x: 0,
                    y: i * (H + GAP),
                    w: Math.max(2, Math.round((b.val / total) * MAX_W)),
                    h: H,
                    color: b.color ?? "#00BFD6",
                    r: 2,
                  })),
                },
                {
                  width: "*",
                  margin: [8, 0, 0, 0],
                  stack: items.map((b) => ({
                    text: `${b.label}  ${b.val}${
                      totalRef ? ` (${Math.round((b.val / total) * 100)}%)` : ""
                    }`,
                    fontSize: 7,
                    color: "#444",
                    margin: [0, 2, 0, GAP - 2],
                  })),
                },
              ],
            },
          ],
        },
        { width: "*", text: "" },
      ],
      margin: [0, 4, 0, 8],
    },
  ];
};

const ANCHO_FOTO = 150;

// pdfmake no envuelve el contenido de un `columns`: cuatro fotos de 150pt más
// sus separaciones se salen del ancho útil de la hoja, así que se parte en filas.
const FOTOS_POR_FILA = 3;

export const GRUPOS_EVIDENCIA = [
  { categoria: "hallazgo", titulo: "Cómo se encontró", color: "#b91c1c" },
  { categoria: "resolucion", titulo: "Cómo quedó resuelto", color: "#15803d" },
];

// Las evidencias anteriores a la separación hallazgo/resolución no traen el
// campo: Mongoose no aplica el default del esquema al leerlas con .lean().
export const categoriaEvidencia = (e) =>
  e?.categoria === "resolucion" ? "resolucion" : "hallazgo";

/**
 * Galería de evidencias fotográficas de registros rechazados, agrupada por punto
 * de control y, dentro de cada punto, por categoría (hallazgo / resolución).
 * Solo pinta las imágenes que el backend devolvió en base64 (`incluirEvidencias: true`).
 *
 * @param {Array} puntos [{ nombre, porcentajeIncumplimiento, evidenciasRechazo:[{base64Completa, categoria, fecha, descripcion, registroNumero}] }]
 */
export const galeriaEvidenciasPdf = (titulo, puntos) => {
  const conFotos = (puntos ?? []).filter((p) =>
    p.evidenciasRechazo?.some((e) => e.base64Completa)
  );
  if (conFotos.length === 0) {
    return [
      { text: titulo, style: "subheader", fontSize: 11, margin: [0, 14, 0, 4] },
      {
        text: "Sin evidencias fotográficas en registros rechazados del período.",
        style: "textoChico",
      },
    ];
  }

  return [
    { text: titulo, style: "subheader", fontSize: 11, margin: [0, 14, 0, 2], pageBreak: "before" },
    {
      text: "Últimas 4 evidencias por categoría (cómo se encontró / cómo quedó resuelto) de registros rechazados por punto de control",
      style: "textoChico",
      margin: [0, 0, 0, 8],
    },
    ...conFotos.flatMap((punto) => {
      const fotos = punto.evidenciasRechazo.filter((e) => e.base64Completa);
      return [
        {
          text: `${punto.nombre}  —  ${punto.porcentajeIncumplimiento}% de incumplimiento`,
          style: "subheader",
          margin: [0, 8, 0, 4],
        },
        ...GRUPOS_EVIDENCIA.flatMap(({ categoria, titulo: tituloGrupo, color }) => {
          const grupo = fotos.filter((e) => categoriaEvidencia(e) === categoria);
          if (grupo.length === 0) return [];

          const filas = [];
          for (let i = 0; i < grupo.length; i += FOTOS_POR_FILA) {
            filas.push({
              columns: grupo.slice(i, i + FOTOS_POR_FILA).map((e) => ({
                width: ANCHO_FOTO,
                stack: [
                  { image: e.base64Completa, width: ANCHO_FOTO, fit: [ANCHO_FOTO, 120] },
                  {
                    text: [
                      e.registroNumero ? `Registro #${e.registroNumero}\n` : "",
                      e.fecha ? `${moment(e.fecha).format("DD/MM/YY HH:mm")}\n` : "",
                      e.descripcion || "",
                    ],
                    fontSize: 6,
                    color: "#555",
                    margin: [0, 3, 0, 0],
                  },
                ],
              })),
              columnGap: 8,
              margin: [0, 0, 0, 6],
            });
          }

          return [
            { text: tituloGrupo, style: "textoChico", bold: true, color, margin: [0, 2, 0, 4] },
            ...filas,
          ];
        }),
      ];
    }),
  ];
};

export const descargarPdfCalidad = ({
  empresa,
  titulo,
  subtitulo,
  extras = [],
  kpis = [],
  preSecciones = [],
  secciones = [],
  postSecciones = [],
  nombreArchivo,
}) => {
  const terceraColumna = {
    border: [false, false, false, false],
    table: {
      headerRows: 1,
      widths: ["100%"],
      body: [
        [{ text: titulo, style: "subheader", alignment: "center" }],
        [{ text: subtitulo ?? "", style: "textoChico", alignment: "center" }],
        [{ text: "Fecha de Impresión:", style: "subheader", alignment: "center" }],
        [{ text: moment().locale("es").format("LL"), style: "textoChico", alignment: "center" }],
        ...extras.map((e) => [{ text: e, style: "textoChico", alignment: "center" }]),
      ],
    },
    layout: "noBorders",
  };

  const dd = {
    pageSize: "LETTER",
    pageOrientation: "landscape",
    pageMargins: [20, 20, 20, 40],
    content: [
      {
        style: "tableExample",
        table: {
          widths: ["18%", "52%", "30%"],
          body: [[...HeaderEmpresaCard(empresa ?? {}, false, false, terceraColumna)]],
        },
      },
      ...tablaKpis(kpis),
      ...preSecciones,
      ...secciones.flatMap(seccionTabla),
      ...postSecciones,
    ],
    images: { logo: empresa?.logo?.base64Completa ?? "" },
    styles: PDF_STYLES,
  };

  pdfMake.createPdf(dd).download(`${nombreArchivo}_${moment().format("YYYY-MM-DD")}`);
};

export const fmtEjecutado = (r) =>
  r?.ejecutadoPor
    ? `${r.ejecutadoPor.nombre ?? ""} ${r.ejecutadoPor.apellidoPaterno ?? ""}`.trim() || "-"
    : "-";

export const fmtFecha = (v) => (v ? moment(v).format("DD/MM/YY HH:mm") : "-");

export const fmtResultado = (v) => RESULTADO_LABEL[v] ?? v ?? "-";

export const fmtDuracion = (v) => (v != null ? `${v} min` : "-");

export const fmtFraccion = (parte, total) => `${parte ?? 0}/${total ?? 0}`;

export const fmtFraccionPct = (parte, total) =>
  total > 0 ? `${parte}/${total} (${Math.round((parte / total) * 100)}%)` : `${parte ?? 0}/${total ?? 0}`;
